import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { games as gamesApi, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Plus, Trash2, Sparkles, GripVertical, Loader2, X as XIcon } from "lucide-react";

const emptyAnswer = () => ({ text: "", points: 0 });
const emptyDecoy = () => ({ text: "" });
const emptyQuestion = () => ({
  id: crypto.randomUUID?.() || String(Math.random()),
  text: "",
  answers: [emptyAnswer(), emptyAnswer(), emptyAnswer(), emptyAnswer(), emptyAnswer()],
  decoys: [emptyDecoy(), emptyDecoy(), emptyDecoy(), emptyDecoy(), emptyDecoy()],
});

export default function GameEditorDialog({ open, onOpenChange, game, onSaved }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [active, setActive] = useState(true);
  const [questions, setQuestions] = useState([emptyQuestion()]);
  const [saving, setSaving] = useState(false);
  const [aiCount, setAiCount] = useState(5);
  const [aiTopic, setAiTopic] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (game) {
      setTitle(game.title || "");
      setDescription(game.description || "");
      setActive(game.active !== false);
      setQuestions(game.questions?.length ? game.questions.map((q) => ({
        ...q,
        answers: q.answers?.length ? q.answers : [emptyAnswer()],
        decoys: q.decoys?.length ? q.decoys : [emptyDecoy()],
      })) : [emptyQuestion()]);
    } else {
      setTitle("");
      setDescription("");
      setActive(true);
      setQuestions([emptyQuestion()]);
    }
  }, [open, game]);

  const setQ = (qi, patch) => setQuestions((qs) => qs.map((q, i) => (i === qi ? { ...q, ...patch } : q)));
  const setAns = (qi, ai, patch) =>
    setQuestions((qs) => qs.map((q, i) => (i === qi ? { ...q, answers: q.answers.map((a, j) => (j === ai ? { ...a, ...patch } : a)) } : q)));
  const addQuestion = () => setQuestions((qs) => [...qs, emptyQuestion()]);
  const removeQuestion = (qi) => setQuestions((qs) => qs.filter((_, i) => i !== qi));
  const addAnswer = (qi) => setQuestions((qs) => qs.map((q, i) => (i === qi ? { ...q, answers: [...q.answers, emptyAnswer()] } : q)));
  const removeAnswer = (qi, ai) => setQuestions((qs) => qs.map((q, i) => (i === qi ? { ...q, answers: q.answers.filter((_, j) => j !== ai) } : q)));
  const setDecoy = (qi, di, text) =>
    setQuestions((qs) => qs.map((q, i) => (i === qi ? { ...q, decoys: (q.decoys || []).map((d, j) => (j === di ? { text } : d)) } : q)));
  const addDecoy = (qi) => setQuestions((qs) => qs.map((q, i) => (i === qi ? { ...q, decoys: [...(q.decoys || []), emptyDecoy()] } : q)));
  const removeDecoy = (qi, di) => setQuestions((qs) => qs.map((q, i) => (i === qi ? { ...q, decoys: (q.decoys || []).filter((_, j) => j !== di) } : q)));

  const runAi = async () => {
    setAiLoading(true);
    try {
      const res = await gamesApi.aiGenerate(game?.id || "new", { count: Number(aiCount) || 5, topic: aiTopic });
      const gen = (res.questions || []).map((q) => ({
        ...q,
        id: q.id || (crypto.randomUUID?.() || String(Math.random())),
        decoys: q.decoys?.length ? q.decoys : [],
      }));
      if (gen.length === 0) return toast.error(t("games.ai_empty"));
      setQuestions((qs) => {
        const base = qs.filter((q) => q.text.trim() || q.answers.some((a) => a.text.trim()));
        return [...base, ...gen];
      });
      toast.success(t("games.ai_added", { count: gen.length }));
    } catch (e) {
      toast.error(formatApiError(e, t("games.ai_error")));
    } finally {
      setAiLoading(false);
    }
  };

  const save = async () => {
    if (!title.trim()) return toast.error(t("games.title_required"));
    const clean = questions
      .map((q) => ({
        id: q.id,
        text: q.text.trim(),
        answers: q.answers.filter((a) => a.text.trim()).map((a) => ({ text: a.text.trim(), points: Number(a.points) || 0 })),
        decoys: (q.decoys || []).filter((d) => d.text.trim()).map((d) => ({ text: d.text.trim() })),
      }))
      .filter((q) => q.text && q.answers.length);
    if (clean.length === 0) return toast.error(t("games.need_one_question"));
    setSaving(true);
    try {
      const payload = { title: title.trim(), description, active, language: "es", questions: clean };
      if (game) await gamesApi.update(game.id, payload);
      else await gamesApi.create(payload);
      toast.success(t("games.saved"));
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e, t("games.save_error")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="game-editor-dialog">
        <DialogHeader>
          <DialogTitle>{game ? t("games.edit_game") : t("games.new_game")}</DialogTitle>
          <DialogDescription>{t("games.editor_hint")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-stone-600">{t("games.game_title")}</label>
            <Input data-testid="game-title-input" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" placeholder="Canales Dijeron" />
          </div>
          <div>
            <label className="text-xs font-semibold text-stone-600">{t("games.game_description")}</label>
            <Input data-testid="game-description-input" value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1" />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={active} onCheckedChange={setActive} data-testid="game-active-switch" />
            <span className="text-sm text-stone-700">{t("games.active")}</span>
          </div>

          {/* AI generator */}
          <div className="rounded-xl border border-stone-200 bg-stone-50 p-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-stone-800">
              <Sparkles size={15} className="text-[#6B3E2E]" /> {t("games.ai_title")}
            </div>
            <p className="text-xs text-stone-500 mt-1">{t("games.ai_desc")}</p>
            <div className="flex flex-wrap items-end gap-2 mt-2">
              <div className="w-20">
                <label className="text-[11px] text-stone-500">{t("games.ai_count")}</label>
                <Input type="number" min={1} max={12} value={aiCount} onChange={(e) => setAiCount(e.target.value)} data-testid="ai-count-input" className="mt-0.5" />
              </div>
              <div className="flex-1 min-w-[140px]">
                <label className="text-[11px] text-stone-500">{t("games.ai_topic")}</label>
                <Input value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} placeholder={t("games.ai_topic_ph")} data-testid="ai-topic-input" className="mt-0.5" />
              </div>
              <Button type="button" onClick={runAi} disabled={aiLoading} data-testid="ai-generate-questions-btn"
                className="bg-gradient-to-r from-[#1E3A8A] to-[#6B3E2E] text-white">
                {aiLoading ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <Sparkles size={15} className="mr-1.5" />}
                {t("games.ai_generate")}
              </Button>
            </div>
          </div>

          {/* Questions */}
          <div className="space-y-3">
            {questions.map((q, qi) => (
              <div key={q.id} className="rounded-xl border border-stone-200 p-3" data-testid={`game-question-${qi}`}>
                <div className="flex items-start gap-2">
                  <GripVertical size={16} className="text-stone-300 mt-2.5" />
                  <div className="flex-1">
                    <Textarea
                      value={q.text}
                      onChange={(e) => setQ(qi, { text: e.target.value })}
                      placeholder={t("games.question_ph")}
                      rows={2}
                      data-testid={`game-question-text-${qi}`}
                    />
                    <div className="mt-2 space-y-1.5">
                      {q.answers.map((a, ai) => (
                        <div key={ai} className="flex items-center gap-2" data-testid={`game-answer-${qi}-${ai}`}>
                          <span className="text-xs text-stone-400 w-4">{ai + 1}</span>
                          <Input
                            value={a.text}
                            onChange={(e) => setAns(qi, ai, { text: e.target.value })}
                            placeholder={t("games.answer_ph")}
                            className="flex-1"
                            data-testid={`game-answer-text-${qi}-${ai}`}
                          />
                          <Input
                            type="number"
                            value={a.points}
                            onChange={(e) => setAns(qi, ai, { points: e.target.value })}
                            className="w-20"
                            placeholder={t("games.points")}
                            data-testid={`game-answer-points-${qi}-${ai}`}
                          />
                          <button onClick={() => removeAnswer(qi, ai)} className="p-1.5 text-stone-400 hover:text-red-600" data-testid={`remove-answer-${qi}-${ai}`}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button onClick={() => addAnswer(qi)} className="mt-2 text-xs font-semibold text-[#1E3A8A] inline-flex items-center gap-1" data-testid={`add-answer-${qi}`}>
                      <Plus size={13} /> {t("games.add_answer")}
                    </button>

                    {/* Decoys (wrong answers) */}
                    <div className="mt-3 rounded-lg border border-red-100 bg-red-50/50 p-2.5" data-testid={`game-decoys-${qi}`}>
                      <div className="text-[11px] font-semibold text-red-700 uppercase tracking-wide">{t("games.decoys_title")}</div>
                      <p className="text-[11px] text-red-500/80 mb-1.5">{t("games.decoys_hint")}</p>
                      <div className="space-y-1.5">
                        {(q.decoys || []).map((d, di) => (
                          <div key={di} className="flex items-center gap-2" data-testid={`game-decoy-${qi}-${di}`}>
                            <XIcon size={13} className="text-red-400 shrink-0" />
                            <Input
                              value={d.text}
                              onChange={(e) => setDecoy(qi, di, e.target.value)}
                              placeholder={t("games.decoy_ph")}
                              className="flex-1"
                              data-testid={`game-decoy-text-${qi}-${di}`}
                            />
                            <button onClick={() => removeDecoy(qi, di)} className="p-1.5 text-stone-400 hover:text-red-600" data-testid={`remove-decoy-${qi}-${di}`}>
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                      <button onClick={() => addDecoy(qi)} className="mt-2 text-xs font-semibold text-red-600 inline-flex items-center gap-1" data-testid={`add-decoy-${qi}`}>
                        <Plus size={13} /> {t("games.add_decoy")}
                      </button>
                    </div>
                  </div>
                  <button onClick={() => removeQuestion(qi)} className="p-1.5 text-stone-400 hover:text-red-600" data-testid={`remove-question-${qi}`}>
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" onClick={addQuestion} data-testid="add-question-btn" className="w-full">
              <Plus size={15} className="mr-1.5" /> {t("games.add_question")}
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={save} disabled={saving} className="brand-button" data-testid="save-game-btn">
            {saving ? <Loader2 size={15} className="animate-spin mr-1.5" /> : null}
            {t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
